import { useState } from "react";
import styled from "@emotion/styled";

import { Button } from "@/components/button";

const Section = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  background: #fff;
`;

const SectionHeader = styled.div<{ collapsed?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ collapsed }) => (collapsed ? "0" : "16px")};
  cursor: pointer;
  user-select: none;

  &:hover {
    opacity: 0.8;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CollapseIcon = styled.span<{ collapsed: boolean }>`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: rotate(${({ collapsed }) => (collapsed ? "0deg" : "90deg")});
`;

const MessageCount = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #64748b;
`;

const ProviderBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  background: #f1f5f9;
  color: #64748b;
`;

const TranscriptContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fafafa;
`;

const TranscriptSegment = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 14px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fafc;
  }
`;

const SpeakerLabel = styled.span<{ channel: number }>`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
  background: ${({ channel }) => (channel === 0 ? "#dbeafe" : "#fef3c7")};
  color: ${({ channel }) => (channel === 0 ? "#1d4ed8" : "#92400e")};
`;

const TranscriptText = styled.span`
  font-size: 13px;
  line-height: 1.5;
  color: #374151;
`;

const PlainTextContainer = styled.div`
  padding: 16px;
  font-size: 14px;
  line-height: 1.7;
  color: #374151;
  white-space: pre-wrap;
`;

const NoTranscriptMessage = styled.div`
  padding: 24px;
  text-align: center;
  color: #64748b;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px dashed #e2e8f0;
`;

const TranscribedAt = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 12px;
`;

interface TranscriptArrayItem {
  sort_key: string;
  transcript: string;
  channel: number;
  disconnected?: boolean;
}

interface TranscriptSectionProps {
  transcriptText: string | null;
  transcriptArray: TranscriptArrayItem[] | null;
  transcriptProvider: string | null;
  transcribedAt: string | null;
}

const formatProvider = (provider: string): string => {
  return provider
    .replace(/_/g, " ")
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getSpeakerLabel = (channel: number): string => {
  if (channel === 0) return "Me";
  return `Speaker ${channel}`;
};

export const TranscriptSection = ({
  transcriptText,
  transcriptArray,
  transcriptProvider,
  transcribedAt,
}: TranscriptSectionProps) => {
  const [showFormatted, setShowFormatted] = useState(true);
  const [collapsed, setCollapsed] = useState(true);

  const hasTranscript = Boolean(transcriptText) || (transcriptArray?.length ?? 0) > 0;
  const hasMultiChannel = transcriptArray?.some(item => item.channel > 0) ?? false;
  const messageCount = transcriptArray?.length ?? 0;

  if (!hasTranscript) {
    return (
      <Section>
        <SectionTitle>Transcript</SectionTitle>
        <NoTranscriptMessage>
          No transcript available for this recording.
          <br />
          <span style={{ fontSize: 12, marginTop: 8, display: "block" }}>
            The audio may still be processing or transcription has not been triggered.
          </span>
        </NoTranscriptMessage>
      </Section>
    );
  }

  const sortedArray = transcriptArray
    ? [...transcriptArray].sort((a, b) => a.sort_key.localeCompare(b.sort_key))
    : null;

  return (
    <Section>
      <SectionHeader collapsed={collapsed} onClick={() => setCollapsed(!collapsed)}>
        <SectionTitle>
          <CollapseIcon collapsed={collapsed}>▶</CollapseIcon>
          Transcript
          {messageCount > 0 && <MessageCount>({messageCount} messages)</MessageCount>}
          {transcriptProvider && (
            <ProviderBadge>{formatProvider(transcriptProvider)}</ProviderBadge>
          )}
          {hasMultiChannel && <ProviderBadge>Multi-channel</ProviderBadge>}
        </SectionTitle>
        {!collapsed && sortedArray && sortedArray.length > 0 && transcriptText && (
          <Button
            type="button"
            variant="soft"
            onClick={e => {
              e.stopPropagation();
              setShowFormatted(!showFormatted);
            }}
          >
            {showFormatted ? "Show Plain Text" : "Show Formatted"}
          </Button>
        )}
      </SectionHeader>

      {!collapsed && (
        <>
          <TranscriptContainer>
            {showFormatted && sortedArray && sortedArray.length > 0 ? (
              sortedArray.map((segment, index) => (
                <TranscriptSegment key={segment.sort_key || index}>
                  <SpeakerLabel channel={segment.channel}>
                    {getSpeakerLabel(segment.channel)}
                  </SpeakerLabel>
                  <TranscriptText>{segment.transcript}</TranscriptText>
                </TranscriptSegment>
              ))
            ) : (
              <PlainTextContainer>{transcriptText}</PlainTextContainer>
            )}
          </TranscriptContainer>

          {transcribedAt && (
            <TranscribedAt>Transcribed: {new Date(transcribedAt).toLocaleString()}</TranscribedAt>
          )}
        </>
      )}
    </Section>
  );
};
